"use client";

import { format } from "date-fns/format";
import { formatDistanceToNow } from "date-fns/formatDistanceToNow";
import { fr } from "date-fns/locale";
import { REGIONS_BY_CODE } from "shared/constants/territoires";

import commonStyles from "./tables/common.module.css";
import styles from "./tables/TraitementTable.module.css";

export function getRegionLabel(code: string) {
  return REGIONS_BY_CODE[code as keyof typeof REGIONS_BY_CODE]?.nom ?? code;
}

export function formatPercentageBadgeSimple(percentage: number) {
  if (percentage >= 70) {
    return <span className={styles.percentageBadgeHigh}>{percentage}%</span>;
  }
  if (percentage > 0) {
    return <span className={styles.percentageBadgeMedium}>{percentage}%</span>;
  }
  return <span className={styles.percentageBadgeZero}>{percentage}%</span>;
}

export function formatActivityDuration(days: number | null): { text: string; className: string } {
  if (days === null) return { text: "-", className: styles.emptyValue };
  if (days <= 0) return { text: "Aujourd'hui", className: styles.activityToday };
  if (days < 30) return { text: `Il y a ${days} jour${days > 1 ? "s" : ""}`, className: styles.activityRecent };
  if (days < 365) {
    const months = Math.floor(days / 30);
    return { text: `Il y a ${months} mois`, className: styles.activityOld };
  }
  const years = Math.floor(days / 365);
  return { text: `Il y a ${years} an${years > 1 ? "s" : ""}`, className: styles.activityOld };
}

export function formatMlActives(count: number) {
  if (count === 0) {
    return <span className={styles.emptyValue}>Aucune ML</span>;
  }
  return count;
}

export function formatDateFr(dateString: string | null): string | null {
  if (!dateString) return null;
  try {
    return format(new Date(dateString), "dd MMMM yyyy", { locale: fr });
  } catch {
    return null;
  }
}

/** Lien vers la fiche CFA ; la provenance alimente le fil d'Ariane (`from`, `q`, `ml_id`). */
export function ficheCfaUrl(organismeId: string, provenance: Record<string, string | null | undefined> = {}) {
  const params = new URLSearchParams();
  Object.entries(provenance).forEach(([cle, valeur]) => valeur && params.set(cle, valeur));
  const query = params.toString();
  return `/suivi-des-indicateurs/cfa/${organismeId}${query ? `?${query}` : ""}`;
}

export function safeHttpUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const { protocol } = new URL(url);
    return protocol === "http:" || protocol === "https:" ? url : null;
  } catch {
    return null;
  }
}

export function formatDateCourte(dateString: string | null): string | null {
  if (!dateString) return null;
  return format(new Date(dateString), "dd/MM/yyyy");
}

export function formatDateWithRelativeTime(dateString: string | null): string {
  if (!dateString) return "Aucune activité";
  try {
    const date = new Date(dateString);
    const formattedDate = format(date, "dd/MM/yyyy", { locale: fr });
    const relativeTime = formatDistanceToNow(date, { locale: fr, addSuffix: true });
    return `${formattedDate}, ${relativeTime}`;
  } catch {
    return "Aucune activité";
  }
}

export function formatDelta(delta: number) {
  if (delta === 0) return <span className={commonStyles.deltaZero}>=</span>;
  if (delta > 0) return <span className={commonStyles.deltaPositive}>+{delta}</span>;
  return <span className={commonStyles.deltaNegative}>{delta}</span>;
}

export function formatVariationBadge(variation: string) {
  const numValue = parseInt(variation.replace(/[+%]/g, ""));
  if (numValue === 0) return <span className={commonStyles.variationZero}>{variation}</span>;
  if (numValue > 0) return <span className={commonStyles.variationPositive}>{variation}</span>;
  return <span className={commonStyles.variationNegative}>{variation}</span>;
}

/** Dernière activité courte : « aujourd'hui », « hier », sinon « le JJ/MM/AAAA » (jours calendaires locaux). */
export function formatDerniereActivite(date: Date | string | null): string {
  if (!date) return "Aucune";
  const jour = new Date(date);
  const debutDuJour = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const ecart = Math.round((debutDuJour(new Date()) - debutDuJour(jour)) / 86_400_000);
  if (ecart <= 0) return "aujourd'hui";
  if (ecart === 1) return "hier";
  return `le ${format(jour, "dd/MM/yyyy")}`;
}
